import { AppLayout } from "@/components/layout/app-layout";
import { PageHeader } from "@/components/layout/page-header";
import { KPICard } from "@/components/ui/kpi-card";
import { RevenueChart } from "@/components/dashboard/revenue-chart";
import { ProfitabilityMatrix } from "@/components/dashboard/profitability-matrix";
import { MandateHealth } from "@/components/dashboard/mandate-health";
import { TeamUtilization } from "@/components/dashboard/team-utilization";
import { StrategicAlerts } from "@/components/dashboard/strategic-alerts";
import { useDashboardMetrics } from "@/hooks/use-dashboard-metrics";
import { Skeleton } from "@/components/ui/skeleton";
import { useMemo } from "react";
import { useAuth } from "@/contexts/auth-context";
import { useValidationStats } from "@/hooks/use-validation-stats";

const Index = () => {
  // Use real data for Executive Dashboard
  const { metrics, isLoading, refetch } = useDashboardMetrics();
  
  // Get real validation stats for pending reviews count
  const { data: validationStats } = useValidationStats();

  const { organization } = useAuth();

  const periodLabel = useMemo(() => {
    const now = new Date();
    const quarter = Math.floor(now.getMonth() / 3) + 1;
    return `Q${quarter} ${now.getFullYear()}`;
  }, []);

  const organizationName = organization?.name ?? "Arshya's Organization";

  // Generate KPI data from real metrics
  const getKpiData = () => {
    if (!metrics) {
      return [];
    }

    const formatCurrency = (amount: number) => {
      return new Intl.NumberFormat('de-CH', {
        style: 'currency',
        currency: 'CHF',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount);
    };

    return [
      {
        icon: <i className="fa-solid fa-sack-dollar" />,
        title: "Total Revenue",
        value: formatCurrency(metrics.totalRevenue),
        trend: {
          value: `${metrics.totalTransactions} transactions`,
          direction: "up" as const
        },
      },
      {
        icon: <i className="fa-solid fa-briefcase" />,
        title: "Active Mandates",
        value: `${metrics.activeMandates} Mandates`,
        trend: {
          value: `${(metrics.averageConfidence * 100).toFixed(1)}% avg confidence`,
          direction: "up" as const
        },
      },
      {
        icon: <i className="fa-solid fa-chart-line" />,
        title: "Automation Rate",
        value: `${metrics.automationRate.toFixed(1)}%`,
        trend: {
          value: `${validationStats?.total || 0} need review`,
          direction: metrics.automationRate >= 80 ? "up" as const : "down" as const
        },
      },
      {
        icon: <i className="fa-solid fa-robot" />,
        title: "Learning Rules",
        value: `${metrics.totalRules} Rules`,
        trend: {
          value: `${metrics.rulesLearnedThisWeek} learned this week`,
          direction: "up" as const
        },
      },
    ];
  };

  return (
    <AppLayout>
      <PageHeader 
        title="Executive Dashboard"
        subtitle={`${organizationName} / ${periodLabel}`}
      />
      <main className="flex-1 p-page-padding lg:p-page-padding-lg overflow-y-auto">

        {/* Strategic KPIs Section */}
        <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-section-gap mb-header-mb">
          {isLoading ? (
            // Loading skeletons
            Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="bg-card p-6 rounded-2xl border">
                <div className="flex items-center justify-between mb-4">
                  <Skeleton className="h-8 w-8 rounded-full" />
                  <Skeleton className="h-4 w-16" />
                </div>
                <Skeleton className="h-6 w-24 mb-2" />
                <Skeleton className="h-8 w-32" />
              </div>
            ))
          ) : (
            // Real KPI data
            getKpiData().map((kpi, index) => (
              <KPICard key={index} {...kpi} variant="compact" />
            ))
          )}
        </section>

        {/* Validation Queue Overview */}
        {validationStats && validationStats.total > 0 && (
          <section className="mb-header-mb">
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-2xl border border-blue-200 dark:border-blue-800 p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                    <i className="fa-solid fa-clipboard-check text-blue-600" />
                    Validation Queue
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {validationStats.total} transactions need review
                  </p>
                </div>
                <a 
                  href="/validation"
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
                >
                  <span>Review Now</span>
                  <i className="fa-solid fa-arrow-right" />
                </a>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-red-200 dark:border-red-800">
                  <div className="flex items-center gap-2 mb-2">
                    <i className="fa-solid fa-exclamation-triangle text-red-600" />
                    <span className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">Critical</span>
                  </div>
                  <div className="text-2xl font-bold text-red-600">
                    {validationStats.critical}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">&lt;50% confidence</p>
                </div>
                
                <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-orange-200 dark:border-orange-800">
                  <div className="flex items-center gap-2 mb-2">
                    <i className="fa-solid fa-exclamation-circle text-orange-600" />
                    <span className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">High</span>
                  </div>
                  <div className="text-2xl font-bold text-orange-600">
                    {validationStats.high}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">50-70% confidence</p>
                </div>
                
                <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-yellow-200 dark:border-yellow-800">
                  <div className="flex items-center gap-2 mb-2">
                    <i className="fa-solid fa-info-circle text-yellow-600" />
                    <span className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">Medium</span>
                  </div>
                  <div className="text-2xl font-bold text-yellow-600">
                    {validationStats.medium}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">70-85% confidence</p>
                </div>
                
                <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-green-200 dark:border-green-800">
                  <div className="flex items-center gap-2 mb-2">
                    <i className="fa-solid fa-check-circle text-green-600" />
                    <span className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">Low</span>
                  </div>
                  <div className="text-2xl font-bold text-green-600">
                    {validationStats.low}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">&ge;85% confidence</p>
                </div>
              </div>
            </div>
          </section>
        )}
        
        {/* Main Dashboard Grid */}
        <section className="grid grid-cols-3 gap-section-gap">
          {/* Left Column */}
          <div className="col-span-3 xl:col-span-2 flex flex-col gap-section-gap">
            <RevenueChart demoMode={false} />
            <ProfitabilityMatrix demoMode={false} />
          </div>
          
          {/* Right Column */}
          <div className="col-span-3 xl:col-span-1 flex flex-col gap-section-gap">
            <MandateHealth demoMode={false} />
            <TeamUtilization demoMode={false} />
            <StrategicAlerts demoMode={false} />
          </div>
        </section>
        
        {/* Footer */}
        <footer className="mt-header-mb pt-section-gap border-t border-brand-border flex justify-between items-center">
          <p className="text-xs text-foreground">Copyright © 2024 Swiss Finance OS</p>
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <span className="hover:text-brand-primary cursor-pointer transition-colors">Privacy Policy</span>
            <span className="hover:text-brand-primary cursor-pointer transition-colors">Terms & Conditions</span>
            <span className="hover:text-brand-primary cursor-pointer transition-colors">Contact</span>
          </div>
        </footer>
      </main>
    </AppLayout>
  );
};

export default Index;
